import React, { useState, useEffect } from "react";
import "../css/session-banner.css";

/**
 * SessionBanner — Sticky countdown bar for session timeout.
 *
 * Two modes:
 * - variant="warning": shown during the 30-minute window before session timeout.
 *   Includes an "Extend session" CTA that calls onExtend().
 * - variant="grace" (default): shown during the 2-hour grace period after timeout.
 *   Countdown only; no extend available (user must re-log).
 *
 * @param {number} timeRemaining — ms remaining in the relevant window
 * @param {string} variant — "warning" | "grace"
 * @param {boolean} isUrgent — true when in last 30 min of grace (grace_urgent)
 * @param {Function} onExtend — called when user clicks "Extend session" (warning only)
 * @param {Function} onReauthenticate — signs out and routes to login (grace only)
 */
const SessionBanner = ({ timeRemaining, graceTimeRemaining, variant = "grace", isUrgent, onExtend, onReauthenticate }) => {
    // Back-compat: old callers pass `graceTimeRemaining`; prefer `timeRemaining` when provided.
    const initialMs = timeRemaining != null ? timeRemaining : graceTimeRemaining;
    const [display, setDisplay] = useState("");
    const [isExtending, setIsExtending] = useState(false);
    const [extendError, setExtendError] = useState("");
    const [isReauthenticating, setIsReauthenticating] = useState(false);
    const [reauthenticateError, setReauthenticateError] = useState("");

    const handleExtend = async () => {
        if (!onExtend || isExtending) return;
        setExtendError("");
        setIsExtending(true);
        try {
            const result = await onExtend();
            if (result === false) {
                throw new Error("Session extension request was not accepted");
            }
        } catch (error) {
            console.error("[SessionBanner] Failed to extend session:", error);
            setExtendError("Unable to extend session. Please try again.");
        } finally {
            setIsExtending(false);
        }
    };

    const handleReauthenticate = async () => {
        if (!onReauthenticate || isReauthenticating) return;
        setReauthenticateError("");
        setIsReauthenticating(true);
        try {
            const result = await onReauthenticate();
            if (result === false) {
                throw new Error("Reauthentication request was not accepted");
            }
        } catch (error) {
            console.error("[SessionBanner] Failed to start reauthentication:", error);
            setReauthenticateError("Unable to sign out. Please try again.");
        } finally {
            setIsReauthenticating(false);
        }
    };

    useEffect(() => {
        if (initialMs == null) return;

        const format = (ms) => {
            const totalSec = Math.max(0, Math.floor(ms / 1000));
            const h = Math.floor(totalSec / 3600);
            const m = Math.floor((totalSec % 3600) / 60);
            const s = totalSec % 60;
            return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
        };

        setDisplay(format(initialMs));

        const tick = setInterval(() => {
            setDisplay((prev) => {
                // Decrement by 1 second from current remaining
                const parts = prev.split(":").map(Number);
                const totalSec = parts[0] * 3600 + parts[1] * 60 + parts[2] - 1;
                if (totalSec <= 0) return "0:00:00";
                const h = Math.floor(totalSec / 3600);
                const m = Math.floor((totalSec % 3600) / 60);
                const s = totalSec % 60;
                return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
            });
        }, 1000);

        return () => clearInterval(tick);
    }, [initialMs]);

    const isWarning = variant === "warning";
    const message = isWarning
        ? "Your session will expire soon. Click Extend to stay logged in."
        : "Session expired. Save your work and log in again.";

    return (
        <div className={`session-banner${isUrgent ? " session-banner-urgent" : ""}${isWarning ? " session-banner-warning" : ""}`}>
            <span className="session-banner-message">{message}</span>
            <span className="session-banner-timer">{display}</span>
            {isWarning && onExtend && (
                <button
                    type="button"
                    className="session-banner-extend-btn"
                    onClick={handleExtend}
                    disabled={isExtending}
                    aria-busy={isExtending}
                >
                    {isExtending ? "Extending…" : "Extend session"}
                </button>
            )}
            {isWarning && extendError && (
                <span className="session-banner-error" role="status" aria-live="polite">
                    {extendError}
                </span>
            )}
            {!isWarning && onReauthenticate && (
                <button
                    type="button"
                    className="session-banner-extend-btn"
                    onClick={handleReauthenticate}
                    disabled={isReauthenticating}
                    aria-busy={isReauthenticating}
                >
                    {isReauthenticating ? "Signing out…" : "Log in again"}
                </button>
            )}
            {!isWarning && reauthenticateError && (
                <span className="session-banner-error" role="status" aria-live="polite">
                    {reauthenticateError}
                </span>
            )}
        </div>
    );
};

export default SessionBanner;
